import {ActionReducer, createFeature, createReducer, createSelector, on, select} from '@ngrx/store';
import {EntityState, EntityAdapter, createEntityAdapter, Dictionary} from '@ngrx/entity';
import {Update} from "@ngrx/entity";
import {uniq, uniqBy} from "lodash";
import {RouteModel} from "../../../routes/models/route.model";
import {RoutesActions, RoutesAPiActions, RoutesChannelActions} from "./routes.action";

export interface RoutesState extends EntityState<RouteModel> {
  cursor: string;
  isLoading: boolean;
  infiniteScrollRouteIds: string[]
}

const adapter: EntityAdapter<RouteModel> = createEntityAdapter<RouteModel>();

const initialState: RoutesState = adapter.getInitialState({
  cursor: null,
  isLoading: false,
  infiniteScrollRouteIds: []
});


const reducer: ActionReducer<RoutesState> = createReducer(
  initialState,

  on(RoutesChannelActions.notificationReceived, (state, {notification}) => {
    if (state.entities[notification.id]?.updatedAt < notification.updatedAt) {
      return adapter.setOne(notification, state);
    } else {
      return state;
    }
  }),

  on(RoutesChannelActions.partialNotificationReceived, (state, {notification}) => {

    if (state.entities[notification.id]?.updatedAt < notification.updatedAt) {
      const changes: Update<RouteModel> = {
        id: notification.id,
        changes: {...notification}
      }
      return adapter.updateOne(changes, state);
    } else {
      return state;
    }
  }),

  on(RoutesAPiActions.getRoute, (state) => ({...state, isLoading: true})),

  on(RoutesAPiActions.getRouteSuccess, (state, {route}) => {
    return adapter.setOne(route, {...state, isLoading: false})
  }),

  on(RoutesAPiActions.getInfiniteScrollRoutes, (state) => ({...state, isLoading: true})),

  on(RoutesAPiActions.getInfiniteScrollRoutesSuccess, (state, {routes, cursor}) => {
    return adapter.setMany(routes,
      {
        ...state,
        infiniteScrollRouteIds: uniq([...state.infiniteScrollRouteIds, ...routes.map(r => r.id)]),
        cursor: cursor,
        isLoading: false
      })
  }),

  on(RoutesAPiActions.getRoutes, (state) => ({...state, isLoading: true})),

  on(RoutesAPiActions.getRoutesSuccess, (state, {routes}) => {
    return adapter.setMany(routes, {...state, isLoading: false})
  }),

  on(RoutesActions.setRoutes, (state, {routes, isAppendFirst}) => {
    if (isAppendFirst) {
      const currentRoutesInStore = Object.values(state.entities);
      const routesArr = uniqBy([...routes, ...currentRoutesInStore], 'id');
      return adapter.setAll(routesArr, state);
    } else {
      return adapter.setMany(routes, state)
    }
  }),

  on(RoutesActions.setInfiniteScrollRoutesIds, (state, {routeIds, isAppendFirst}) => {
    let newState = state;
    const currentInfiniteScrollRouteIdsInStore = state.infiniteScrollRouteIds;

    if (isAppendFirst) {
      newState = {
        ...newState,
        infiniteScrollRouteIds: uniq([...routeIds, ...currentInfiniteScrollRouteIdsInStore])
      }
    } else {
      newState = {
        ...newState,
        infiniteScrollRouteIds: uniq([...currentInfiniteScrollRouteIdsInStore, ...routeIds])
      }
    }
    return newState;

  }),

  on(RoutesActions.removeRoutes, (state, {ids}) => adapter.removeMany(ids, state)),

  on(RoutesActions.removeAll, (state) => adapter.removeAll(state)),

  on(RoutesActions.clearStore, () => ({...initialState}))
);

export const routesFeature = createFeature({
  name: 'Routes',
  reducer,
  extraSelectors: ({
                     selectRoutesState,
                     selectEntities,
                     selectCursor,
                     selectIsLoading,
                     selectInfiniteScrollRouteIds,
                   }) => ({
    ...adapter.getSelectors(selectRoutesState),
    selectRoutes: () => createSelector(
      selectEntities,
      (entities) => Object.values(entities),
    ),
    selectInfiniteScrollRoutes: () => createSelector(
      selectEntities,
      selectInfiniteScrollRouteIds,
      (entities, infiniteScrollRouteIds) => {
        const infiniteScrollRoutes = [];
        infiniteScrollRouteIds.forEach(id => {
          if (!!entities[id]) {
            infiniteScrollRoutes.push(entities[id]);
          }
        })
        return infiniteScrollRoutes;
      },
    ),
    selectRoutesById: (ids: string[]) => createSelector(
      selectEntities,
      (entities) => {
        const routes = [];
        ids.forEach(id => {
          if (!!entities[id]) {
            routes.push(entities[id]);
          }
        })
        return routes;
      },
    ),
    selectRoute: (id: string) => createSelector(
      selectEntities,
      (entities: Dictionary<RouteModel>) => entities[id],
    ),
    selectCursor: () => createSelector(
      selectCursor,
      (cursor) => cursor
    ),
    selectIsLoading: () => createSelector(
      selectIsLoading,
      (isLoading) => isLoading
    )
  })
});
